// Engineering > Audio Lab: the research instrument for Learn's pitch work (issue #3374). Runs the
// capture pipeline (mic -> worklet -> worker -> detectors) with switchable constraints, plays guide
// tones through the speaker, and runs the measurement protocols whose numbers decide the design:
// round-trip latency, echo-cancellation depth, and double-talk tracking. Session logs and recordings
// post to the dev server (server/src/audio_lab_routes.ts). Recordings have two channels, the
// microphone and the guide the lab played (issue #3708), and a double-talk run can record itself to
// make the offline canceller's fixtures. The "own echo canceller" toggle runs the app's own canceller
// on the capture as the Learn tab does on Android, so the tests measure the shipped path; the readout
// then has a line about it. See docs/learn/audio-research.md.

import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AudioSessionType, readAudioSessionType } from '../../../../audio/audio_session.ts';
import {
  frequencyToMidi,
  median,
  midiToFrequency,
  midiToNoteName,
  nearestSemitone,
} from '../../../../audio/pitch/notes.ts';
import { defaultDetectorConfig, DetectorConfig } from '../../../../audio/pitch/types.ts';
import { EngineeringAdminWrapper, EngineeringButtonWrapper, SubHeader } from '../shared.tsx';
import {
  AudioLabEngine,
  DoubleTalkPhase,
  DoubleTalkResult,
  getNativeAudioInfo,
  GuideKind,
  hasIosAudioOverride,
  IosAudioSessionOverride,
  LabSessionEntry,
  LeakResult,
  MicOptions,
  RoundTripResult,
  setIosAudioSessionOverride,
  StopMode,
  voicedClarity,
  voicedDbfs,
} from './audio_lab_engine.ts';
import { cancellerReadout } from './lab_canceller.ts';

const traceSeconds = 8;
const traceSemitones = 26;

interface Readout {
  note: string;
  cents: string;
  hz: string;
  clarity: string;
  mpm: string;
  target: string;
  level: string;
  compute: string;
  latency: string;
  rate: string;
  canceller: string; // empty unless the own canceller runs
}

const emptyReadout: Readout = {
  note: '--',
  cents: '',
  hz: '',
  clarity: '',
  mpm: '',
  target: '',
  level: '',
  compute: '',
  latency: '',
  rate: '',
  canceller: '',
};

function micLabel(mic: MicOptions): string {
  return `EC ${mic.echoCancellation ? 'on' : 'off'}, NS ${mic.noiseSuppression ? 'on' : 'off'}, AGC ${
    mic.autoGainControl ? 'on' : 'off'
  }${mic.ownCanceller ? ', own canceller' : ''}${mic.contextAfterMic ? ', context after mic' : ''}`;
}

function percent(value: number | undefined): string {
  return value === undefined ? '' : `${Math.round(value * 100)}%`;
}

// A double-talk phase's own-canceller medians, when the canceller ran
function phaseEchoLabel(phase: DoubleTalkPhase): string {
  const level = (dbfs: number | undefined) => (dbfs === undefined ? 'n/a' : `${dbfs.toFixed(0)}`);
  const delay = phase.delayMsMedian === undefined ? 'no delay' : `delay ${phase.delayMsMedian.toFixed(0)} ms`;
  return `canceller ${delay}, mic ${level(phase.micDbfsMedian)} echo ${level(phase.echoDbfsMedian)} residual ${level(
    phase.residualDbfsMedian,
  )} dBFS, cancelled ${percent(phase.cancelledRate)}`;
}

export const AudioLabPage = () => {
  const [engine] = useState(() => new AudioLabEngine());
  const [running, setRunning] = useState(false);
  const [mic, setMic] = useState<MicOptions>({
    echoCancellation: true,
    noiseSuppression: false,
    autoGainControl: false,
    contextAfterMic: false,
    ownCanceller: false,
  });
  const [detector, setDetector] = useState<DetectorConfig>({ ...defaultDetectorConfig });
  const [guideKind, setGuideKind] = useState<GuideKind>('piano');
  const [guideMidi, setGuideMidi] = useState(57); // A3
  const [guideGain, setGuideGain] = useState(0.5);
  const [guideSetsTarget, setGuideSetsTarget] = useState(true);
  const [targetMidi, setTargetMidi] = useState<number | undefined>(undefined);
  const [readout, setReadout] = useState<Readout>(emptyReadout);
  const [events, setEvents] = useState<LabSessionEntry[]>([]);
  const [busy, setBusy] = useState<string | undefined>(undefined);
  const [phaseText, setPhaseText] = useState('');
  const [status, setStatus] = useState('');
  const [roundTrips, setRoundTrips] = useState<Array<{ id: number; label: string; result: RoundTripResult }>>([]);
  const [leaks, setLeaks] = useState<Array<{ id: number; label: string; results: LeakResult[] }>>([]);
  const [doubleTalks, setDoubleTalks] = useState<Array<{ id: number; label: string; result: DoubleTalkResult }>>([]);
  const [nativeInfo, setNativeInfo] = useState<unknown>(undefined);
  const [recordLabel, setRecordLabel] = useState('clip');
  const [recordDoubleTalk, setRecordDoubleTalk] = useState(false);
  const [sessionPath, setSessionPath] = useState('');
  const [iosOverride] = useState(() => hasIosAudioOverride());
  const [sessionType, setSessionType] = useState<AudioSessionType>('playback');
  const [sessionAtCreation, setSessionAtCreation] = useState(false);
  const [stopMode, setStopMode] = useState<StopMode>('keepContextSuspended');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rateRef = useRef({ count: 0, at: 0 });

  useEffect(() => {
    engine.onLog = (entry) => setEvents((previous) => [entry, ...previous].slice(0, 40));
    return () => {
      engine.onLog = undefined;
      void engine.stop();
    };
  }, [engine]);

  // readout at 10 Hz (React state), trace at display rate (canvas, no React)
  useEffect(() => {
    rateRef.current = { count: engine.resultCount, at: performance.now() };
    const interval = setInterval(() => {
      const result = engine.latestResult;
      if (!running || !result) {
        return;
      }
      const now = performance.now();
      const elapsed = (now - rateRef.current.at) / 1000;
      const rate = elapsed > 0 ? (engine.resultCount - rateRef.current.count) / elapsed : 0;
      rateRef.current = { count: engine.resultCount, at: now };
      const voiced = result.yin && result.yin.clarity >= voicedClarity && result.dbfs > voicedDbfs;
      const semitone = result.yin ? nearestSemitone(result.yin.frequency) : undefined;
      setReadout({
        note: voiced && semitone ? midiToNoteName(semitone.midi) : '--',
        cents: voiced && semitone ? `${semitone.cents >= 0 ? '+' : ''}${semitone.cents.toFixed(0)} cents` : '',
        hz: result.yin ? `${result.yin.frequency.toFixed(1)} Hz` : '',
        clarity: result.yin ? `yin clarity ${result.yin.clarity.toFixed(2)}` : 'yin off',
        mpm: result.mpm ? `mpm ${result.mpm.frequency.toFixed(1)} Hz (${result.mpm.clarity.toFixed(2)})` : 'mpm off',
        target: result.target
          ? `target ${result.target.cents >= 0 ? '+' : ''}${result.target.cents.toFixed(0)} cents (clarity ${result.target.clarity.toFixed(2)}, 8vb ${result.target.octaveBelow.toFixed(2)}, 8va ${result.target.octaveAbove.toFixed(2)})`
          : 'no target',
        level: `${result.dbfs === -Infinity ? '-inf' : result.dbfs.toFixed(0)} dBFS`,
        compute: `${result.computeMs.toFixed(1)} ms compute (yin ${result.yinMs?.toFixed(1) ?? '-'}, mpm ${result.mpmMs?.toFixed(1) ?? '-'}, target ${result.targetMs?.toFixed(1) ?? '-'})`,
        latency: `${engine.lastResultLatencyMs.toFixed(0)} ms frame-end to main thread`,
        rate: `${rate.toFixed(0)} frames/s, ${engine.workletStats?.framesDropped ?? 0} dropped`,
        canceller: cancellerReadout(result) ?? '',
      });
    }, 100);
    return () => clearInterval(interval);
  }, [engine, running]);

  useEffect(() => {
    let frame = 0;
    let tick = 0;
    const draw = () => {
      frame = requestAnimationFrame(draw);
      tick += 1;
      if (tick % 3 !== 0) {
        return; // about 20 fps is plenty for the trace and keeps slow phones' main threads free
      }
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }
      drawTrace(canvas, engine, targetMidi);
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [engine, targetMidi]);

  const guard = useCallback(async (label: string, action: () => Promise<void>) => {
    setBusy(label);
    setStatus('');
    try {
      await action();
    } catch (error) {
      setStatus(`${label}: ${(error as Error).message ?? String(error)}`);
    } finally {
      setBusy(undefined);
    }
  }, []);

  const onStart = () =>
    guard('start', async () => {
      await engine.start(mic, detector);
      setRunning(true);
      setNativeInfo(await getNativeAudioInfo());
    });

  const onStop = () =>
    guard('stop', async () => {
      await engine.stop();
      setRunning(false);
      setReadout(emptyReadout);
    });

  const applyTarget = (midi: number | undefined) => {
    setTargetMidi(midi);
    engine.setTargetFrequency(midi === undefined ? undefined : midiToFrequency(midi));
  };

  const onPlayGuide = (durationSec?: number) =>
    guard('guide', async () => {
      if (guideSetsTarget) {
        applyTarget(guideMidi);
      }
      await engine.playGuide({ kind: guideKind, midi: guideMidi, gain: guideGain }, durationSec);
    });

  const onRoundTrip = () =>
    guard('round trip', async () => {
      setPhaseText('Measuring round-trip latency: keep quiet');
      const result = await engine.measureRoundTrip(5, (done) => setPhaseText(`click ${done} of 5`));
      setRoundTrips((previous) => [{ id: Date.now(), label: micLabel(mic), result }, ...previous]);
      setPhaseText('');
    });

  const onLeak = () =>
    guard('cancellation', async () => {
      const stimuli = [
        { label: 'sine A3', kind: 'sine' as const, midi: 57, gain: guideGain },
        { label: 'sine A4', kind: 'sine' as const, midi: 69, gain: guideGain },
        { label: 'piano A3', kind: 'piano' as const, midi: 57, gain: guideGain },
        { label: 'piano A4', kind: 'piano' as const, midi: 69, gain: guideGain },
      ];
      const results = await engine.measureLeak(stimuli, (label) => setPhaseText(`Playing ${label}: keep quiet`));
      setLeaks((previous) => [{ id: Date.now(), label: micLabel(mic), results }, ...previous]);
      setPhaseText('');
    });

  const onDoubleTalk = () =>
    guard('double talk', async () => {
      const result = await engine.runDoubleTalk(
        { kind: guideKind, gain: guideGain },
        (instruction, secondsLeft) => setPhaseText(secondsLeft > 0 ? `${instruction} (${secondsLeft})` : instruction),
        recordDoubleTalk ? { label: recordLabel } : undefined,
      );
      setDoubleTalks((previous) => [{ id: Date.now(), label: `${micLabel(mic)}, ${guideKind}`, result }, ...previous]);
    });

  const onRecord = (seconds: number) =>
    guard('record', async () => {
      engine.startRecording();
      for (let left = seconds; left > 0; left -= 1) {
        setPhaseText(`Recording "${recordLabel}" (${left})`);
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
      const recording = await engine.stopRecording();
      setPhaseText('Uploading');
      const path = await engine.uploadRecording(recordLabel, recording, {
        label: recordLabel,
        mic,
        detector: engine.detectorConfig,
        guide: engine.activeGuideOptions,
        context: engine.getContextInfo(),
        track: engine.trackSettings,
        userAgent: navigator.userAgent,
        native: nativeInfo,
      });
      setPhaseText(`Saved ${path}`);
    });

  const onSendSession = () =>
    guard('send log', async () => {
      setSessionPath(await engine.uploadSession());
    });

  const onCopySession = () =>
    guard('copy log', async () => {
      await navigator.clipboard.writeText(JSON.stringify(engine.session, null, 2));
      setStatus('Session JSON copied');
    });

  const onNativeInfo = () =>
    guard('native info', async () => {
      const info = await getNativeAudioInfo();
      setNativeInfo(info ?? { platform: 'browser (no wrapper bridge found)' });
      engine.log('native.audioInfo', info);
    });

  const onIosOverride = (override: IosAudioSessionOverride) =>
    guard('audio session override', async () => {
      const info = await setIosAudioSessionOverride(override);
      setNativeInfo(info);
      engine.log('native.audioSessionOverride', info);
    });

  const contextInfo = engine.getContextInfo();

  return (
    <EngineeringAdminWrapper title={'Audio Lab'} allowInDev>
      <Page>
        <Panel>
          <SubHeader>Microphone</SubHeader>
          <Row>
            <Toggle
              label="echo cancellation"
              value={mic.echoCancellation}
              disabled={running}
              onChange={(v) => setMic({ ...mic, echoCancellation: v })}
            />
            <Toggle
              label="own echo canceller"
              value={mic.ownCanceller}
              disabled={running}
              onChange={(v) => setMic({ ...mic, ownCanceller: v })}
            />
            <Toggle
              label="noise suppression"
              value={mic.noiseSuppression}
              disabled={running}
              onChange={(v) => setMic({ ...mic, noiseSuppression: v })}
            />
            <Toggle
              label="auto gain"
              value={mic.autoGainControl}
              disabled={running}
              onChange={(v) => setMic({ ...mic, autoGainControl: v })}
            />
            <Toggle
              label="create context after mic"
              value={mic.contextAfterMic}
              disabled={running}
              onChange={(v) => setMic({ ...mic, contextAfterMic: v })}
            />
          </Row>
          <Row>
            {running ? (
              <Button onClick={onStop} disabled={busy !== undefined}>
                Stop mic
              </Button>
            ) : (
              <Button onClick={onStart} disabled={busy !== undefined}>
                Start mic
              </Button>
            )}
            <Button onClick={onNativeInfo} disabled={busy !== undefined}>
              Native audio info
            </Button>
            <label>
              stop mode{' '}
              <select
                value={stopMode}
                onChange={(event) => {
                  const mode = event.target.value as StopMode;
                  setStopMode(mode);
                  engine.stopMode = mode;
                  engine.log('stopMode', mode);
                }}
              >
                <option value="closeAll">close context + stop track</option>
                <option value="keepContext">keep context, stop track</option>
                <option value="keepContextSuspended">keep context suspended, stop track</option>
                <option value="keepTrack">close context, keep track (disabled)</option>
                <option value="keepAll">keep both, disconnect only</option>
              </select>
            </label>
          </Row>
          {iosOverride ? (
            <Row>
              <Button onClick={() => onIosOverride({ port: 'speaker' })} disabled={busy !== undefined}>
                Route: speaker
              </Button>
              <Button onClick={() => onIosOverride({ port: 'none' })} disabled={busy !== undefined}>
                Route: default
              </Button>
              <Button
                onClick={() =>
                  onIosOverride({
                    category: 'playAndRecord',
                    mode: 'default',
                    defaultToSpeaker: true,
                    allowBluetooth: true,
                  })
                }
                disabled={busy !== undefined}
              >
                playAndRecord + defaultToSpeaker
              </Button>
              <Button
                onClick={() =>
                  onIosOverride({
                    category: 'playAndRecord',
                    mode: 'voiceChat',
                    defaultToSpeaker: true,
                    allowBluetooth: true,
                  })
                }
                disabled={busy !== undefined}
              >
                playAndRecord voiceChat + speaker
              </Button>
              <Button
                onClick={() => onIosOverride({ category: 'playback', active: true })}
                disabled={busy !== undefined}
              >
                Host: playback active
              </Button>
              <Button onClick={() => onIosOverride({ active: false })} disabled={busy !== undefined}>
                Host: deactivate
              </Button>
            </Row>
          ) : null}
          <Small>
            {contextInfo
              ? `context ${contextInfo.state}, ${contextInfo.sampleRate} Hz, base latency ${contextInfo.baseLatencyMs.toFixed(1)} ms, output latency ${
                  contextInfo.outputLatencyMs === undefined ? 'n/a' : contextInfo.outputLatencyMs.toFixed(1) + ' ms'
                }`
              : 'no audio context yet'}
          </Small>
          <Small>{engine.trackSettings ? `track: ${JSON.stringify(engine.trackSettings)}` : ''}</Small>
        </Panel>

        <Panel>
          <BigNote>{readout.note}</BigNote>
          <Mono>
            {readout.cents} {readout.hz} {readout.clarity}
          </Mono>
          <Mono>{readout.mpm}</Mono>
          <Mono>{readout.target}</Mono>
          <Mono>
            {readout.level} {readout.compute} {readout.rate}
          </Mono>
          <Mono>{readout.latency}</Mono>
          {readout.canceller ? <Mono>{readout.canceller}</Mono> : null}
          <Canvas ref={canvasRef} />
          {phaseText ? <Phase>{phaseText}</Phase> : null}
          {status ? <Status>{status}</Status> : null}
        </Panel>

        <Panel>
          <SubHeader>Audio session (iOS)</SubHeader>
          <Row>
            <label>
              type{' '}
              <select value={sessionType} onChange={(event) => setSessionType(event.target.value as AudioSessionType)}>
                {(['auto', 'playback', 'play-and-record', 'ambient', 'transient', 'transient-solo'] as const).map(
                  (type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ),
                )}
              </select>
            </label>
            <Button onClick={() => engine.setAudioSessionType(sessionType)}>Apply now</Button>
            <Toggle
              label="also request before the context is created"
              value={sessionAtCreation}
              onChange={(value) => {
                setSessionAtCreation(value);
                engine.audioSessionTypeAtCreation = value ? sessionType : undefined;
              }}
            />
          </Row>
          <Row>
            <Button onClick={() => guard('keep-alive', () => engine.startKeepAlive())} disabled={busy !== undefined}>
              Silent element on
            </Button>
            <Button onClick={() => engine.stopKeepAlive()}>Silent element off</Button>
            <Button
              onClick={() => guard('piano series', () => engine.playPianoSeries(20, 1.5, 57, 0.6))}
              disabled={busy !== undefined}
            >
              Piano every 1.5 s for 30 s
            </Button>
            <Button
              onClick={() => guard('metronome', () => engine.playMetronome(100, 60))}
              disabled={busy !== undefined}
            >
              Metronome 60 s
            </Button>
            <Button onClick={() => engine.stopScheduled()}>Stop scheduled</Button>
          </Row>
          <Small>
            navigator.audioSession: {readAudioSessionType()}. Flip the silent switch or lock the phone while a series
            plays; the event log records visibility and context state changes.
          </Small>
        </Panel>

        <Panel>
          <SubHeader>Guide</SubHeader>
          <Row>
            <select value={guideKind} onChange={(event) => setGuideKind(event.target.value as GuideKind)}>
              <option value="piano">piano sample</option>
              <option value="sine">sine</option>
              <option value="triangle">triangle</option>
            </select>
            <Button onClick={() => setGuideMidi(guideMidi - 12)}>-8va</Button>
            <Button onClick={() => setGuideMidi(guideMidi - 1)}>-1</Button>
            <NoteLabel>
              {midiToNoteName(guideMidi)} ({midiToFrequency(guideMidi).toFixed(1)} Hz)
            </NoteLabel>
            <Button onClick={() => setGuideMidi(guideMidi + 1)}>+1</Button>
            <Button onClick={() => setGuideMidi(guideMidi + 12)}>+8va</Button>
          </Row>
          <Row>
            <label>
              gain {guideGain.toFixed(2)}
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={guideGain}
                onChange={(event) => setGuideGain(Number(event.target.value))}
              />
            </label>
            <Toggle label="guide sets detector target" value={guideSetsTarget} onChange={setGuideSetsTarget} />
          </Row>
          <Row>
            <Button onClick={() => onPlayGuide()} disabled={busy !== undefined}>
              Play (hold)
            </Button>
            <Button onClick={() => onPlayGuide(1)} disabled={busy !== undefined}>
              Play 1 s
            </Button>
            <Button onClick={() => engine.stopGuide()}>Stop guide</Button>
            <Button onClick={() => applyTarget(guideMidi)}>Target = guide</Button>
            <Button onClick={() => applyTarget(undefined)}>Clear target</Button>
          </Row>
          <Small>
            target:{' '}
            {targetMidi === undefined
              ? 'none'
              : `${midiToNoteName(targetMidi)} (${midiToFrequency(targetMidi).toFixed(1)} Hz)`}
          </Small>
        </Panel>

        <Panel>
          <SubHeader>Detector</SubHeader>
          <Row>
            <Toggle label="YIN" value={detector.enableYin} onChange={(v) => updateDetector({ enableYin: v })} />
            <Toggle label="MPM" value={detector.enableMpm} onChange={(v) => updateDetector({ enableMpm: v })} />
            <NumberField
              label="window"
              value={detector.windowSize}
              step={256}
              onChange={(v) => updateDetector({ windowSize: v })}
            />
            <NumberField
              label="hop"
              value={detector.hopSize}
              step={128}
              onChange={(v) => updateDetector({ hopSize: v })}
            />
            <NumberField
              label="min Hz"
              value={detector.minFrequency}
              step={10}
              onChange={(v) => updateDetector({ minFrequency: v })}
            />
            <NumberField
              label="max Hz"
              value={detector.maxFrequency}
              step={50}
              onChange={(v) => updateDetector({ maxFrequency: v })}
            />
            <NumberField
              label="yin threshold"
              value={detector.yinThreshold}
              step={0.05}
              onChange={(v) => updateDetector({ yinThreshold: v })}
            />
            <NumberField
              label="silence dBFS"
              value={detector.silenceDbfs}
              step={5}
              onChange={(v) => updateDetector({ silenceDbfs: v })}
            />
          </Row>
        </Panel>

        <Panel>
          <SubHeader>Tests</SubHeader>
          <Row>
            <Button onClick={onRoundTrip} disabled={!running || busy !== undefined}>
              Round-trip latency
            </Button>
            <Button onClick={onLeak} disabled={!running || busy !== undefined}>
              Cancellation depth
            </Button>
            <Button onClick={onDoubleTalk} disabled={!running || busy !== undefined}>
              Double talk
            </Button>
            <Toggle
              label="record double talk (mic + guide WAV)"
              value={recordDoubleTalk}
              disabled={busy !== undefined}
              onChange={setRecordDoubleTalk}
            />
          </Row>
          <Row>
            <input
              value={recordLabel}
              onChange={(event) => setRecordLabel(event.target.value)}
              placeholder="clip label"
            />
            <Button onClick={() => onRecord(5)} disabled={!running || busy !== undefined}>
              Record 5 s
            </Button>
            <Button onClick={() => onRecord(15)} disabled={!running || busy !== undefined}>
              Record 15 s
            </Button>
          </Row>
          {roundTrips.map(({ id, label, result }) => (
            <Result key={id}>
              {`round trip [${label}]: median ${result.medianMs === undefined ? 'n/a' : result.medianMs.toFixed(0) + ' ms'}; samples ${result.samplesMs.map((v) => v.toFixed(0)).join(', ')} ms; timeouts ${result.timeouts}; threshold ${result.threshold.toFixed(3)}`}
            </Result>
          ))}
          {leaks.map(({ id, label, results }) => (
            <Result key={id}>
              cancellation [{label}]:
              {results.map((r) => (
                <div key={r.label}>
                  {r.label}: leak {r.leakDb === Infinity ? 'inf' : r.leakDb.toFixed(1)} dB above floor (
                  {r.noiseDbfs.toFixed(0)} to {r.levelDbfs.toFixed(0)} dBFS), yin lock {percent(r.yinLockRate)}, target
                  clarity {r.targetClarity?.toFixed(2) ?? 'n/a'}, {r.frames} frames
                </div>
              ))}
            </Result>
          ))}
          {doubleTalks.map(({ id, label, result }) => (
            <Result key={id}>
              double talk [{label}]:{' '}
              {result.aborted
                ? `aborted: ${result.aborted}`
                : `singer ${result.singerHz?.toFixed(1)} Hz (${midiToNoteName(result.singerMidi ?? 0)}), other note ${midiToNoteName(result.otherMidi ?? 0)}`}
              {result.phases.map((phase) => (
                <div key={phase.label}>
                  {phase.label}: voiced {percent(phase.voicedRate)}, on singer {percent(phase.onSingerRate)}, on guide{' '}
                  {percent(phase.onGuideRate)}, target on singer {percent(phase.targetOnSingerRate)}, {phase.frames}{' '}
                  frames
                  {phase.cancelledRate === undefined ? null : `; ${phaseEchoLabel(phase)}`}
                </div>
              ))}
              {result.recording ? <div>recording: {result.recording}</div> : null}
              {result.recordingError ? <div>recording not saved: {result.recordingError}</div> : null}
            </Result>
          ))}
        </Panel>

        <Panel>
          <SubHeader>Session</SubHeader>
          <Row>
            <Button onClick={onSendSession} disabled={busy !== undefined}>
              Send log to dev server
            </Button>
            <Button onClick={onCopySession} disabled={busy !== undefined}>
              Copy JSON
            </Button>
          </Row>
          {sessionPath ? <Small>saved: {sessionPath}</Small> : null}
          <Small>{navigator.userAgent}</Small>
          {nativeInfo !== undefined ? <Pre>{JSON.stringify(nativeInfo, null, 2)}</Pre> : null}
          <EventList>
            {events.map((entry) => (
              <li key={entry.seq}>
                <Mono>
                  {entry.at.slice(11, 23)} {entry.kind} {entry.data === undefined ? '' : JSON.stringify(entry.data)}
                </Mono>
              </li>
            ))}
          </EventList>
        </Panel>
      </Page>
    </EngineeringAdminWrapper>
  );

  function updateDetector(partial: Partial<DetectorConfig>) {
    const next = { ...detector, ...partial };
    setDetector(next);
    engine.setDetectorConfig(partial);
  }
};

function drawTrace(canvas: HTMLCanvasElement, engine: AudioLabEngine, targetMidi: number | undefined) {
  const dpr = window.devicePixelRatio || 1;
  const width = canvas.clientWidth;
  const height = canvas.clientHeight;
  if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
    canvas.width = width * dpr;
    canvas.height = height * dpr;
  }
  const g = canvas.getContext('2d');
  if (!g) {
    return;
  }
  g.setTransform(dpr, 0, 0, dpr, 0, 0);
  g.clearRect(0, 0, width, height);
  const context = engine.audioContext;
  if (!context) {
    return;
  }
  const now = context.currentTime;
  const results = engine.resultsBetween(now - traceSeconds, now);
  const voicedMidis = results
    .filter((r) => r.yin && r.yin.clarity >= voicedClarity && r.dbfs > voicedDbfs)
    .map((r) => frequencyToMidi(r.yin!.frequency));
  const center = targetMidi ?? median(voicedMidis) ?? 60;
  const low = center - traceSemitones / 2;
  const yFor = (midi: number) => height - ((midi - low) / traceSemitones) * height;
  const xFor = (time: number) => ((time - (now - traceSeconds)) / traceSeconds) * width;

  g.font = '10px sans-serif';
  for (let midi = Math.ceil(low); midi <= low + traceSemitones; midi += 1) {
    const y = yFor(midi);
    const isC = midi % 12 === 0;
    g.strokeStyle = isC ? 'rgba(128,128,128,0.6)' : 'rgba(128,128,128,0.2)';
    g.beginPath();
    g.moveTo(0, y);
    g.lineTo(width, y);
    g.stroke();
    if (isC || midi === targetMidi) {
      g.fillStyle = 'rgba(128,128,128,0.9)';
      g.fillText(midiToNoteName(midi), 2, y - 2);
    }
  }
  if (targetMidi !== undefined) {
    const y = yFor(targetMidi);
    g.strokeStyle = 'rgba(40,160,80,0.9)';
    g.lineWidth = 2;
    g.beginPath();
    g.moveTo(0, y);
    g.lineTo(width, y);
    g.stroke();
    g.lineWidth = 1;
  }
  for (const result of results) {
    const x = xFor(result.time);
    if (result.mpm) {
      g.fillStyle = `rgba(230,140,30,${Math.max(0.15, result.mpm.clarity).toFixed(2)})`;
      g.fillRect(x - 1, yFor(frequencyToMidi(result.mpm.frequency)) - 1, 2, 2);
    }
    if (result.yin) {
      g.fillStyle = `rgba(40,110,220,${Math.max(0.15, result.yin.clarity).toFixed(2)})`;
      g.beginPath();
      g.arc(x, yFor(frequencyToMidi(result.yin.frequency)), 2.5, 0, Math.PI * 2);
      g.fill();
    }
    if (result.target && targetMidi !== undefined) {
      g.fillStyle = `rgba(40,160,80,${Math.max(0.15, result.target.clarity).toFixed(2)})`;
      g.fillRect(x - 1, yFor(targetMidi + result.target.cents / 100) - 1, 3, 3);
    }
  }
}

const Toggle = ({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}) => (
  <ToggleLabel>
    <input type="checkbox" checked={value} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
    {label}
  </ToggleLabel>
);

const NumberField = ({
  label,
  value,
  step,
  onChange,
}: {
  label: string;
  value: number;
  step: number;
  onChange: (value: number) => void;
}) => (
  <ToggleLabel>
    {label}
    <NumberInput type="number" value={value} step={step} onChange={(event) => onChange(Number(event.target.value))} />
  </ToggleLabel>
);

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 12px 40px;
  max-width: 900px;
  margin: 0 auto;
`;

const Panel = styled.div`
  border: 1px solid var(--color-text);
  border-radius: 10px;
  padding: 8px 12px;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 14px;
  margin: 6px 0;
`;

const Button = styled(EngineeringButtonWrapper)`
  font-size: 1rem;
  margin: 2px 0;
  padding: 6px 12px;
  transition:
    transform 80ms ease,
    background-color 80ms ease;

  &:active {
    transform: scale(0.94);
    background-color: rgba(128, 128, 128, 0.25);
  }

  &:disabled {
    opacity: 0.45;
  }
`;

const ToggleLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.95rem;
`;

const NumberInput = styled.input`
  width: 5.5em;
`;

const NoteLabel = styled.span`
  font-family: monospace;
  font-size: 1.1rem;
  min-width: 9em;
  text-align: center;
`;

const BigNote = styled.div`
  font-size: 3rem;
  font-family: monospace;
  text-align: center;
  line-height: 1.1;
`;

const Mono = styled.div`
  font-family: monospace;
  font-size: 0.8rem;
  white-space: pre-wrap;
  word-break: break-word;
`;

const Small = styled.div`
  font-size: 0.75rem;
  opacity: 0.8;
  word-break: break-word;
`;

const Pre = styled.pre`
  font-size: 0.7rem;
  white-space: pre-wrap;
  word-break: break-word;
  text-align: left;
`;

const Canvas = styled.canvas`
  width: 100%;
  height: 220px;
  display: block;
  margin-top: 6px;
  background: rgba(128, 128, 128, 0.08);
  border-radius: 6px;
`;

// fixed so the instruction stays visible wherever the page is scrolled during a test
const Phase = styled.div`
  position: fixed;
  top: 3.5rem;
  left: 0;
  right: 0;
  z-index: 10;
  font-size: 1.3rem;
  text-align: center;
  padding: 12px;
  background: rgba(40, 160, 80, 0.92);
  color: white;
`;

const Status = styled.div`
  color: #c0392b;
  text-align: center;
  padding: 4px;
`;

const Result = styled.div`
  font-family: monospace;
  font-size: 0.8rem;
  text-align: left;
  padding: 6px 0;
  border-top: 1px dashed rgba(128, 128, 128, 0.5);
  word-break: break-word;
`;

const EventList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 8px 0 0;
  text-align: left;
  max-height: 300px;
  overflow: auto;
`;
